/**
 * Shared activity timestamp parsing and local-date helpers.
 * Mirrors backend/time_normalization.py where possible.
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const TIMESTAMP_INTERPRETATION = {
  UTC_INSTANT: "utc_instant",
  NAIVE_LOCAL: "naive_local_wall_clock",
};

export function parseActivityInstant(raw) {
  if (raw == null || raw === "") return null;
  const text = String(raw).trim();
  if (!text) return null;
  const parsed = dayjs(text);
  return parsed.isValid() ? parsed : null;
}

function hasTimezoneSuffix(raw) {
  return /[zZ]|[+-]\d{2}:?\d{2}$/.test(String(raw || ""));
}

function naiveLocalDateKeyFromString(raw) {
  const match = String(raw || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function naiveLocalHourFromString(raw) {
  const match = String(raw || "").match(/T(\d{2}):/);
  return match ? Number(match[1]) : null;
}

export function getActivityUtcOffsetMinutes(activity = {}) {
  if (Number.isFinite(activity.utc_offset_minutes)) {
    return activity.utc_offset_minutes;
  }
  return null;
}

export function activityHasAbsoluteInstant(activity = {}) {
  if (activity.timestamp_interpretation === TIMESTAMP_INTERPRETATION.NAIVE_LOCAL) {
    return false;
  }
  const raw = activity.start_time;
  if (!raw) return false;
  return hasTimezoneSuffix(raw) && Boolean(parseActivityInstant(raw));
}

/**
 * Fallback order: local_start_date → start_time_local date → offset-aware start_time → UTC start_time.
 * Never parses naive start_time through browser-local Date semantics.
 */
export function getActivityLocalDateKey(activity = {}) {
  if (activity.local_start_date) return String(activity.local_start_date);
  if (activity.start_time_local) {
    const local = parseActivityInstant(activity.start_time_local);
    if (local) return local.format("YYYY-MM-DD");
  }
  if (activity.timestamp_interpretation === TIMESTAMP_INTERPRETATION.NAIVE_LOCAL) {
    return null;
  }
  const raw = activity.start_time;
  const instant = parseActivityInstant(raw);
  if (!instant) return null;
  const offset = getActivityUtcOffsetMinutes(activity);
  if (offset != null && hasTimezoneSuffix(raw)) {
    return instant.utcOffset(offset).format("YYYY-MM-DD");
  }
  if (hasTimezoneSuffix(raw)) {
    return instant.utc().format("YYYY-MM-DD");
  }
  return naiveLocalDateKeyFromString(raw);
}

/**
 * Fallback order: start_time_local hour → offset-adjusted aware start_time → UTC start_time hour.
 */
export function getActivityLocalHour(activity = {}) {
  if (activity.start_time_local) {
    const local = parseActivityInstant(activity.start_time_local);
    if (local) return local.hour();
  }
  if (activity.timestamp_interpretation === TIMESTAMP_INTERPRETATION.NAIVE_LOCAL) {
    return null;
  }
  const raw = activity.start_time;
  const instant = parseActivityInstant(raw);
  if (!instant) return null;
  const offset = getActivityUtcOffsetMinutes(activity);
  if (offset != null && hasTimezoneSuffix(raw)) {
    return instant.utcOffset(offset).hour();
  }
  if (hasTimezoneSuffix(raw)) {
    return instant.utc().hour();
  }
  return naiveLocalHourFromString(raw);
}

/**
 * Returns epoch ms for aware UTC instants only. Naive/unresolved activities return null.
 */
export function getActivitySortInstant(activity = {}) {
  if (!activityHasAbsoluteInstant(activity)) return null;
  const instant = parseActivityInstant(activity.start_time);
  return instant ? instant.valueOf() : null;
}

export function getActivitySortKey(activity = {}) {
  const tie = String(activity.id || activity._id || "");
  if (activityHasAbsoluteInstant(activity)) {
    return { kind: "instant", ms: getActivitySortInstant(activity), tie };
  }
  return {
    kind: "local",
    date: getActivityLocalDateKey(activity) || "",
    local: String(activity.start_time_local || ""),
    tie,
  };
}

/** Newest-first ordering using UTC instants for aware activities; naive uses local wall-clock only. */
export function compareActivitiesByStart(a, b) {
  const tieBreak = (left, right) =>
    String(right.id || right._id || "").localeCompare(String(left.id || left._id || ""));

  const awareA = activityHasAbsoluteInstant(a);
  const awareB = activityHasAbsoluteInstant(b);
  if (awareA && awareB) {
    const instantA = getActivitySortInstant(a);
    const instantB = getActivitySortInstant(b);
    if (instantA !== instantB) return instantB - instantA;
    return tieBreak(a, b);
  }

  if (awareA !== awareB) {
    return awareA ? -1 : 1;
  }

  const localA = a.start_time_local || getActivityLocalDateKey(a) || "";
  const localB = b.start_time_local || getActivityLocalDateKey(b) || "";
  if (localA !== localB) return localB.localeCompare(localA);
  return tieBreak(a, b);
}

export function formatActivityDate(activity, options = {}) {
  const key = getActivityLocalDateKey(activity);
  if (!key) return "—";
  const parsed = dayjs(`${key}T12:00:00`);
  if (!parsed.isValid()) return "—";
  return parsed.format(options.format || "MMM D, YYYY");
}

function hasUsableClockTime(raw) {
  return /T\d{2}:\d{2}/.test(String(raw || ""));
}

function formatWallClockFromTimestamp(raw, clockFormat) {
  const match = String(raw || "").match(/T(\d{2}):(\d{2})/);
  if (!match) return "";
  return dayjs(`2000-01-01T${match[1]}:${match[2]}:00`).format(clockFormat);
}

/**
 * Athlete-local occurrence clock time. Returns "" when no usable time exists.
 * Does not invent midnight from a date-only value.
 */
export function formatActivityLocalTime(activity, options = {}) {
  const clockFormat = options.format || "h:mm A";
  if (hasUsableClockTime(activity.start_time_local)) {
    return formatWallClockFromTimestamp(activity.start_time_local, clockFormat);
  }
  if (activity.timestamp_interpretation === TIMESTAMP_INTERPRETATION.NAIVE_LOCAL) {
    return "";
  }
  const raw = activity.start_time;
  if (!hasUsableClockTime(raw)) return "";
  if (!hasTimezoneSuffix(raw)) {
    return formatWallClockFromTimestamp(raw, clockFormat);
  }
  const instant = parseActivityInstant(raw);
  if (!instant) return "";
  const offset = getActivityUtcOffsetMinutes(activity);
  const display =
    offset != null ? instant.utcOffset(offset) : instant.utc();
  return display.format(clockFormat);
}

/** Date, or "date · time" from occurrence fields. Date-only when time is missing. */
export function formatActivityOccurrenceStamp(activity) {
  const date = formatActivityDate(activity);
  if (!date || date === "—") return "—";
  const time = formatActivityLocalTime(activity);
  if (!time) return date;
  return `${date} · ${time}`;
}

/**
 * Fallback order: start_time_local → offset-aware start_time → UTC start_time.
 */
export function formatActivityDateTime(activity, options = {}) {
  if (activity.start_time_local) {
    const local = parseActivityInstant(activity.start_time_local);
    if (local) return local.format(options.format || "MMM D, YYYY h:mm A");
  }
  if (activity.timestamp_interpretation === TIMESTAMP_INTERPRETATION.NAIVE_LOCAL) {
    return "—";
  }
  const raw = activity.start_time;
  const instant = parseActivityInstant(raw);
  if (!instant) return "—";
  const offset = getActivityUtcOffsetMinutes(activity);
  const display =
    offset != null && hasTimezoneSuffix(raw) ? instant.utcOffset(offset) : instant.utc();
  return display.format(options.format || "MMM D, YYYY h:mm A");
}

export function formatOffsetLabel(activity = {}) {
  const offset = getActivityUtcOffsetMinutes(activity);
  if (offset == null) return null;
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset);
  const hours = String(Math.floor(abs / 60)).padStart(2, "0");
  const minutes = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hours}:${minutes}`;
}

export function isValidActivityTimestamp(activity = {}) {
  return Boolean(getActivityLocalDateKey(activity));
}
